import axios from "axios";
import { toast } from "sonner";
import { LOCKED_HINT } from "@/lib/tmAdapters";

// Backend base URL comes ONLY from env (never hardcoded). Phase 1 points this at the
// Travel-Matching API, which is now the single source of truth for the web client.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const WS_URL = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws`;
export const TOKEN_KEY = "voyalyo_token";

export const api = axios.create({ baseURL: API, timeout: 30000 });

// ── Phase 2B-Read Write-Guard ────────────────────────────────────────────────
// During the read-only test phase, block ALL writes (POST/PUT/PATCH/DELETE) app-wide
// BEFORE they hit the network. Only auth login is exempt (logout is client-only).
// Flip WRITE_GUARD_READONLY to false to re-enable writes in a later phase.
export const WRITE_GUARD_READONLY = false;
const WRITE_METHODS = ["post", "put", "patch", "delete"];
const AUTH_ALLOWLIST = ["/auth/login"];

api.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;

  const method = (config.method || "get").toLowerCase();
  if (WRITE_GUARD_READONLY && WRITE_METHODS.includes(method)) {
    const url = config.url || "";
    const allowed = AUTH_ALLOWLIST.some((p) => url.startsWith(p) || url.includes(p));
    if (!allowed) {
      toast(LOCKED_HINT);
      const err = new Error("write-guard-blocked");
      err.__writeGuardBlocked = true;
      return Promise.reject(err); // aborted before any network call; no body logged
    }
  }
  return config;
});

// ── Globaler 401-Handler (abgelaufene/ungültige Sitzung, z. B. Alt-Token ohne sid) ──
// AuthContext registriert hier eine Funktion, die Token+State leert, den Cache räumt,
// zum Login umleitet und EINMALIG einen Hinweis zeigt. Auth-Endpunkte sind ausgenommen,
// damit z. B. ein falsches Login-Passwort (401) nicht als Sitzungsablauf behandelt wird.
let onUnauthorized = null;
export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}
// Age-1: registrierter Handler für 403-Antworten (Alters-/Einschränkungscodes).
let onForbidden = null;
export function setForbiddenHandler(fn) {
  onForbidden = fn;
}
const AUTH_401_EXEMPT = ["/auth/login", "/auth/register", "/auth/forgot-password", "/auth/reset-password"];

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const exempt = AUTH_401_EXEMPT.some((p) => url.includes(p));
    if (status === 401 && !exempt && localStorage.getItem(TOKEN_KEY) && onUnauthorized) {
      onUnauthorized();
    }
    if (status === 403 && onForbidden) {
      onForbidden(err);
    }
    return Promise.reject(err);
  }
);

export async function getSocketToken() {
  return localStorage.getItem(TOKEN_KEY);
}

// DEV-ONLY: record GET-response metadata for the in-app diagnostics panel.
// Dynamic import keeps this out of production runtime/behavior.
if (process.env.NODE_ENV === "development") {
  api.interceptors.response.use(
    (res) => {
      if ((res.config?.method || "get").toLowerCase() === "get") {
        import("@/lib/devDiagnostics").then((m) => m.recordDiag(res.config?.url, res.status, res.data)).catch(() => {});
      }
      return res;
    },
    (err) => {
      if ((err.config?.method || "get").toLowerCase() === "get") {
        import("@/lib/devDiagnostics").then((m) => m.recordDiag(err.config?.url, err.response?.status || 0, err.response?.data)).catch(() => {});
      }
      return Promise.reject(err);
    }
  );
}
